import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { Event, Session, Trap, Stats, TrapType } from '../types';

const MAX_EVENTS = 300;
const POLL_INTERVAL = 5000; // 5 seconds

function computeAction(doc: any): Event['action'] {
  const path = String(doc.path || '');
  const method = String(doc.method || '');
  const status = Number(doc.status ?? doc.statusCode ?? 0);
  const classification = String(doc.classification || '');

  // Login attempts
  if (path === '/api/auth/login' && method === 'POST') return 'LOGIN_ATTEMPT';

  // Already trapped => reroute behavior
  if (classification === 'trapped') return 'REROUTE';

  // Brute force / suspicious
  if (classification === 'brute_force' || status === 401 || status === 403) return 'SUSPICIOUS_BEHAVIOR';
  if (classification === 'suspicious') return 'SUSPICIOUS_BEHAVIOR';

  // Sensitive paths
  const sensitive = ['/admin', '/wp-admin', '/.env', '/backup', '/config'];
  if (sensitive.some(p => path.startsWith(p))) return 'FILE_ACCESS';

  return 'REQUEST';
}

function deriveEventStatus(classification?: string, statusCode?: number): Event['status'] {
  const c = String(classification || '').toLowerCase();

  if (c === 'trapped') return 'trapped';
  if (c === 'suspicious' || c === 'brute_force') return 'suspicious';

  // fallback: treat repeated auth failures as suspicious-looking event
  if (statusCode === 401 || statusCode === 403) return 'suspicious';

  return 'normal';
}

function deriveTrapType(classification?: string): TrapType | undefined {
  const c = String(classification || '').toLowerCase();
  if (c === 'trapped') return 'WEB';
  return undefined;
}

function mapEventFromDb(doc: any): Event {
  const id = doc._id ? String(doc._id) : (doc.id || `evt-${Date.now()}`);

  const timestamp =
    typeof doc.timestamp === 'string' ? doc.timestamp : new Date(doc.timestamp).toISOString();

  const classification = doc.classification || 'normal';
  const statusCode = typeof doc.status === 'number' ? doc.status : Number(doc.status ?? 0);

  const action = computeAction(doc);
  const status = deriveEventStatus(classification, statusCode);
  const trapType = deriveTrapType(classification);

  const meta = {
    ...(doc.raw && typeof doc.raw === 'object' ? { raw: doc.raw } : {}),
    requestHost: doc.requestHost,
    method: doc.method,
    status: doc.status,
    userAgent: doc.userAgent,
    classification,
  };

  return {
    id,
    timestamp,
    sourceIp: doc.sourceIp || '',
    path: doc.path || '',
    sessionId: doc.sessionId || '',
    severity: doc.severity,
    method: doc.method,
    statusCode,
    classification,
    action,
    status,
    trapType,
    meta,
  };
}

function mapSessionFromDb(doc: any, eventCount: number): Session {
  const firstSeen =
    typeof doc.firstSeen === 'string'
      ? doc.firstSeen
      : new Date(doc.firstSeen || doc.createdAt || Date.now()).toISOString();

  const lastSeen =
    typeof doc.lastSeen === 'string'
      ? doc.lastSeen
      : new Date(doc.lastSeen || doc.updatedAt || Date.now()).toISOString();

  const statusRaw = String(doc.status || 'normal').toLowerCase();
  const status: Session['status'] =
    statusRaw === 'trapped' ? 'trapped' : statusRaw === 'suspicious' ? 'suspicious' : 'normal';

  return {
    sessionId: doc.sessionId,
    sourceIp: doc.sourceIp,
    status,
    failedLogins: doc.failedLogins || 0,
    firstSeen,
    lastActivity: lastSeen,
    eventCount,
    userAgent: doc.lastUserAgent || doc.userAgent,
    trapType: status === 'trapped' ? 'WEB' : undefined,
  };
}

function mapTrapFromDb(doc: any): Trap {
  const id = doc._id ? String(doc._id) : (doc.id || `trap-${Date.now()}`);

  const activatedAt =
    typeof doc.activatedAt === 'string'
      ? doc.activatedAt
      : new Date(doc.activatedAt || doc.createdAt || Date.now()).toISOString();

  return {
    id,
    type: (doc.type as TrapType) || 'WEB',
    sessionId: doc.sessionId || 'unknown',
    sourceIp: doc.sourceIp || '',
    startTime: activatedAt,
    status: doc.active ? 'RUNNING' : 'STOPPED',
    label: doc.label || 'Web Trap',
    containerId: doc.containerId,
    meta: doc.meta || {},
  };
}

function buildStats(events: Event[], sessions: Session[], traps: Trap[]): Stats {
  return {
    totalRequests: events.length,
    suspiciousSessions: sessions.filter(s => s.status === 'suspicious').length,
    trappedSessions: sessions.filter(s => s.status === 'trapped').length,
    activeTraps: traps.filter(t => t.status === 'RUNNING').length,
  };
}

interface UseEventStreamReturn {
  events: Event[];
  sessions: Session[];
  traps: Trap[];
  stats: Stats;
  connected: boolean;
  clearEvents: () => void;
  reconnect: () => void;
}

export const useEventStream = (): UseEventStreamReturn => {
  const [events, setEvents] = useState<Event[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [traps, setTraps] = useState<Trap[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalRequests: 0,
    suspiciousSessions: 0,
    activeTraps: 0,
    trappedSessions: 0,
  });
  const [connected, setConnected] = useState(false);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const token = useMemo(() => localStorage.getItem('token') || '', []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const loadData = useCallback(async () => {
    if (!token) return;

    try {
      const [eventsRaw, sessionsRaw, trapsRaw, statsRaw] = await Promise.all([
        api.getEvents(token, 200),
        api.getSessions(token, 200),
        api.getTraps(token, 200),
        api.getStats(token),
      ]);

      const mappedEvents: Event[] = (eventsRaw || []).map(mapEventFromDb);

      // Count events per session
      const counts = new Map<string, number>();
      for (const e of mappedEvents) {
        counts.set(e.sessionId, (counts.get(e.sessionId) || 0) + 1);
      }

      const mappedSessions: Session[] = (sessionsRaw || []).map((s: any) =>
        mapSessionFromDb(s, counts.get(s.sessionId) || 0)
      );

      const mappedTraps: Trap[] = (trapsRaw || []).map(mapTrapFromDb);

      setEvents(mappedEvents.slice(0, MAX_EVENTS));
      setSessions(mappedSessions);
      setTraps(mappedTraps);

      setStats({
        totalRequests: statsRaw?.totalRequests ?? mappedEvents.length,
        suspiciousSessions: statsRaw?.suspiciousSessions ?? mappedSessions.filter(s => s.status === 'suspicious').length,
        activeTraps: statsRaw?.activeTraps ?? mappedTraps.filter(t => t.status === 'RUNNING').length,
        trappedSessions: statsRaw?.trappedSessions ?? mappedSessions.filter(s => s.status === 'trapped').length,
      });

      setConnected(true);
    } catch (e) {
      console.error('Failed to load dashboard data:', e);
      setConnected(false);
    }
  }, [token]);

  const reconnect = useCallback(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!token) return;

    // Initial load
    loadData();

    // Poll every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      loadData();
    }, POLL_INTERVAL);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [token, loadData]);

  return {
    events,
    sessions,
    traps,
    stats,
    connected,
    clearEvents,
    reconnect,
  };
};
